import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {DataSource, IListItemState} from '@mintdata/mint-kit/api/models/List';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {ContextMenu, IContextMenuItem, ToggleOn} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./ValueFormatMenu.less');
const cx = classnames.bind(styles);

export interface IValueFormatMenuProps
{

}

export interface IValueFormatMenuState
{
    isOpened:boolean;
}

export interface IValueFormatMenuItem extends IContextMenuItem
{
    valueFormat?:IMeshCellValueFormat,
    formatSample?:string,
}

function getFormatSample(valueFormat:IMeshCellValueFormat, value:IValue)
{
    return valueFormat.formatValue(value).toString();
}

const menuItems:Array<IValueFormatMenuItem> = [
    {
        label: 'Automatic',
        valueFormat: MeshCellValueFormat.General
    },
    {
        label: 'Plain Text',
        valueFormat: MeshCellValueFormat.PlainText
    },
    {
        isSeparator: true
    },
    {
        label: 'Number',
        valueFormat: MeshCellValueFormat.Number,
        formatSample: getFormatSample(MeshCellValueFormat.Number, ANumber.fromNumber(1000.12)) //'1,000.12',
    },
    {
        label: 'Percent',
        valueFormat: MeshCellValueFormat.Percent,
        formatSample: getFormatSample(MeshCellValueFormat.Percent, ANumber.fromNumber(0.1012)) //'10.12%',
    },
    {
        isSeparator: true
    },
    {
        label: 'Accounting',
        valueFormat: MeshCellValueFormat.Accounting,
        formatSample: getFormatSample(MeshCellValueFormat.Accounting, ANumber.fromNumber(-1000.12)) //'$(1,000.12)',
    },
    {
        label: 'Financial',
        valueFormat: MeshCellValueFormat.Financial,
        formatSample: getFormatSample(MeshCellValueFormat.Financial, ANumber.fromNumber(-1000.12)) //'(1,000.12)',
    },
    {
        label: 'Currency',
        valueFormat: MeshCellValueFormat.Currency,
        formatSample: getFormatSample(MeshCellValueFormat.Currency, ANumber.fromNumber(1000.12)) //'$1,000.12',
    },
    {
        label: 'Currency (rounded)',
        valueFormat: MeshCellValueFormat.CurrencyRounded,
        formatSample: getFormatSample(MeshCellValueFormat.CurrencyRounded, ANumber.fromNumber(1000.12)) //'$1,000',
    },
    {
        isSeparator: true
    },
    {
        label: 'Date',
        valueFormat: MeshCellValueFormat.Date,
        formatSample: getFormatSample(MeshCellValueFormat.Date, ANumber.fromNumber(39716)) //'9/26/2008',
    },
    {
        label: 'Time',
        valueFormat: MeshCellValueFormat.Time,
        formatSample: getFormatSample(MeshCellValueFormat.Time, ANumber.fromNumber(0.665972222222222)) //'3:59:00 PM',
    },
    {
        label: 'Date time',
        valueFormat: MeshCellValueFormat.DateTime,
        formatSample: getFormatSample(MeshCellValueFormat.DateTime, ANumber.fromNumber(39716.665972222222222)) //'09/26/2008 15:59:00',
    },
    {
        label: 'Duration',
        valueFormat: MeshCellValueFormat.Duration,
        formatSample: getFormatSample(MeshCellValueFormat.Duration, ANumber.fromNumber(1.00069444444445)) //'24:01:00',
    },
    {
        label: 'Timestamp',
        valueFormat: MeshCellValueFormat.Timestamp,
        formatSample: getFormatSample(MeshCellValueFormat.Timestamp, ADateTime.fromJSDate(new Date(1561641341814))) //'06/27/2019 16:16:16.814',
    },
    {
        isSeparator: true
    },
    {
        label: 'Image',
        valueFormat: MeshCellValueFormat.Image
    }
];

@observer
export class ValueFormatMenu extends React.Component<IValueFormatMenuProps, IValueFormatMenuState>
{
    public constructor(props:any)
    {
        super(props);

        this.state = {
            isOpened: false
        };
    }

    private _onShow = () => this.setState({isOpened: true});
    private _onHide = () =>
    {
        this.setState({isOpened: false});

        const {mesh} = controllers.ui.spreadsheets;
        mesh.focusMeshOrControl(this._focusedMeshCtx);
    };

    public render():JSX.Element
    {
        if( !this._focusedMeshState )
            return null;

        const togglerId = 'valueFormatSelector';

        const format = this._getFormat();
        const activeCellValueFormat = format || MeshCellValueFormat.General;

        let iValueFormatMenuItem = menuItems.find(item => item.valueFormat && item.valueFormat.kind == activeCellValueFormat.kind);
        let label;
        if( iValueFormatMenuItem )
        {
            label = iValueFormatMenuItem.label;
        }
        else
        {
            label = 'User Defined';
        }

        const {isOpened} = this.state;

        return (
            <div className={cx('base')} id={togglerId}>
                <ContextMenu
                    toggleOn={[ToggleOn.CLICK]}
                    togglersIds={[togglerId]}
                    popupAnchor={PopupAnchor.RightTop}
                    sourceAnchor={SourceAnchor.RightBottom}
                    popupClassName={cx('menuPopup')}
                    dataSource={this._menuProvider}
                    itemRenderer={this._renderSheetsMenuItem}
                    onShow={this._onShow}
                    onHide={this._onHide}
                >
                    <div className={cx('handler', {isOpened})}>
                        {label}
                        <MDIcon
                            className={cx('menuIcon', {isOpened})}
                            name={MDIcons.common.ArrowDropdown}
                            size={MDIconSize.medium20}
                        />
                    </div>
                </ContextMenu>
            </div>
        );
    }

    @bind
    private _menuProvider():DataSource<IValueFormatMenuItem>
    {
        const format = this._getFormat();
        const activeCellValueFormat = format || MeshCellValueFormat.General;

        return menuItems.map((item:IValueFormatMenuItem) =>
        {
            if( item.isSeparator ) return item;

            const isSelected = item.valueFormat.kind == activeCellValueFormat.kind;

            return {
                ...item,
                isSelected,
                handler: () => this._selectMenuItem(item)
            } as IValueFormatMenuItem;
        });
    }

    private _selectMenuItem(item:IValueFormatMenuItem)
    {
        const focusedMeshCtx = this._focusedMeshCtx;

        controllers.ui.spreadsheets.meshCellSettings.setValueFormat(focusedMeshCtx, item.valueFormat);

        const {mesh} = controllers.ui.spreadsheets;
        mesh.focusMeshOrControl(focusedMeshCtx);
    }

    @bind
    private _renderSheetsMenuItem({label, isSeparator, formatSample}:IValueFormatMenuItem,
                                  {isSelected}:IListItemState,
                                  {menuItem, separator}:IStyles):JSX.Element
    {
        if( isSeparator ) return (
            <div className={cx(separator)} />
        );

        return (
            <div className={cx('menuItem', menuItem)}>

                <div className={cx('menuItemIcon')}>
                    {
                        isSelected &&
                        <MDIcon
                            name={MDIcons.common.Checkmark}
                            size={MDIconSize.medium16}
                            className={cx('icon')}
                        />
                    }
                </div>

                <div className={cx('menuItemLabel')}>
                    {label}
                </div>

                {
                    formatSample &&
                    <div className={cx('menuItemFormatSample')}>
                        {formatSample}
                    </div>
                }
            </div>
        );
    }

    private _getSettings():MeshCellSettings
    {
        const {spreadsheets} = controllers.ui;

        if( !this._focusedMeshCtx )
            return null;

        const {sheetSettingsCtrl} = this._focusedMeshCtx;

        const meshState = spreadsheets.mesh.getState(this._focusedMeshCtx);
        const activeCell = meshState.details.activeCell;

        return sheetSettingsCtrl.getCellSettings(this._focusedSheetId, activeCell.asKey());
    }

    private _getFormat():IMeshCellValueFormat
    {
        const {spreadsheets} = controllers.ui;

        if( !this._focusedMeshCtx )
            return null;

        const {sheetSettingsCtrl} = this._focusedMeshCtx;

        const meshState = spreadsheets.mesh.getState(this._focusedMeshCtx);
        const activeCell = meshState.details.activeCell;

        return sheetSettingsCtrl.getCellFormat(this._focusedSheetId, activeCell.asKey());
    }

    @computed
    private get _focusedSheetId():SheetId
    {
        const {main} = controllers.ui.spreadsheets;

        const workbook = main.getRootWorkbook();
        if( !workbook ) return;

        return workbook.view.focusedSheetId;
    }

    @computed
    private get _focusedMeshCtx():IWSPMeshContext
    {
        return controllers.ui.spreadsheets.workbook.getMeshContextBySheetId(this._focusedSheetId);
    }

    @computed
    private get _focusedMeshState():MeshState
    {
        return this._focusedMeshCtx && controllers.ui.spreadsheets.mesh.getState(this._focusedMeshCtx);
    }
}
